export const HALF_TERMS = ['Autumn 1', 'Autumn 2', 'Spring 1', 'Spring 2', 'Summer 1', 'Summer 2'];

const HALF_TERM_ORDER = Object.fromEntries(HALF_TERMS.map((term, index) => [term, index]));

export function getAcademicYear(date = new Date()) {
  const year = date.getFullYear();
  const month = date.getMonth();
  return month >= 8 ? `${year}/${String(year + 1).slice(-2)}` : `${year - 1}/${String(year).slice(-2)}`;
}

export function getCurrentHalfTerm(date = new Date()) {
  const month = date.getMonth();
  if (month <= 1) return 'Spring 1';
  if (month <= 3) return 'Spring 2';
  if (month <= 5) return 'Summer 1';
  if (month <= 7) return 'Summer 2';
  if (month <= 9) return 'Autumn 1';
  return 'Autumn 2';
}

/** Normalise academic year strings to a comparable start/end pair. */
export function normalizeAcademicYear(value) {
  if (!value || typeof value !== 'string') return null;
  const trimmed = value.trim();

  let match = trimmed.match(/^(\d{4})\/(\d{2})$/);
  if (match) {
    const start = Number(match[1]);
    const end = Number(`${String(start).slice(0, 2)}${match[2]}`);
    return { start, end, label: trimmed };
  }

  match = trimmed.match(/^(\d{4})-(\d{4})$/);
  if (match) {
    return { start: Number(match[1]), end: Number(match[2]), label: trimmed };
  }

  match = trimmed.match(/^(\d{4})-(\d{2})$/);
  if (match) {
    const start = Number(match[1]);
    const end = Number(`${String(start).slice(0, 2)}${match[2]}`);
    return { start, end, label: trimmed };
  }

  return { start: null, end: null, label: trimmed };
}

export function academicYearsMatch(a, b) {
  if (!a || !b) return false;
  if (String(a).trim() === String(b).trim()) return true;

  const left = normalizeAcademicYear(a);
  const right = normalizeAcademicYear(b);
  if (!left || !right || left.start == null || right.start == null) {
    return left?.label === right?.label;
  }
  return left.start === right.start && left.end === right.end;
}

export function getRecordHalfTerm(record) {
  return record?.halfTerm || record?.term || null;
}

export function getRecordTargets(record) {
  if (Array.isArray(record?.targets) && record.targets.length) return record.targets;
  if (record?.target) return [record.target];
  return [];
}

export function getRecordProgressSummary(record) {
  const targets = getRecordTargets(record);
  const completed = Array.isArray(record?.completedTargets) ? record.completedTargets.length : 0;
  const partial = Array.isArray(record?.partiallyMetTargets) ? record.partiallyMetTargets.length : 0;
  return { total: targets.length, completed, partial };
}

export function formatTermLabel(record) {
  const halfTerm = getRecordHalfTerm(record);
  const academicYear = record?.academicYear;
  return [halfTerm, academicYear].filter(Boolean).join(' · ');
}

function compareRecords(a, b) {
  const yearA = normalizeAcademicYear(a?.academicYear);
  const yearB = normalizeAcademicYear(b?.academicYear);
  if (yearA?.start != null && yearB?.start != null && yearA.start !== yearB.start) {
    return yearB.start - yearA.start;
  }

  const termA = HALF_TERM_ORDER[getRecordHalfTerm(a)] ?? -1;
  const termB = HALF_TERM_ORDER[getRecordHalfTerm(b)] ?? -1;
  if (termA !== termB) return termB - termA;

  const updatedA = a?.updatedAt ? new Date(a.updatedAt).getTime() : 0;
  const updatedB = b?.updatedAt ? new Date(b.updatedAt).getTime() : 0;
  return updatedB - updatedA;
}

export function groupTargetsByTerm(records = []) {
  return [...records].sort(compareRecords);
}

export function isCurrentTermRecord(record, academicYear = getAcademicYear(), halfTerm = getCurrentHalfTerm()) {
  return academicYearsMatch(record?.academicYear, academicYear)
    && getRecordHalfTerm(record) === halfTerm;
}

export function splitCurrentAndArchiveRecords(records = [], academicYear = getAcademicYear(), halfTerm = getCurrentHalfTerm()) {
  const sorted = groupTargetsByTerm(records);
  const current = sorted.filter((record) => isCurrentTermRecord(record, academicYear, halfTerm));
  const archive = sorted.filter((record) => !isCurrentTermRecord(record, academicYear, halfTerm));
  return { current, archive };
}
